import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Eye, EyeOff, LogIn } from "lucide-react";
import { toast } from "sonner";
import { auth, authApi } from "@/lib/api";
import { getApiError as formatApiError } from "@/lib/utils";

/** Email + password sign-in. JWT is set as an httpOnly cookie by
 *  /api/auth/login so the frontend never touches the raw token. */
export default function Login() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || !password) {
      toast.error("Enter your email and password");
      return;
    }
    setSubmitting(true);
    try {
      const user = await authApi.login(email.trim().toLowerCase(), password);
      auth.set(user);
      toast.success(`Welcome, ${user.display_name || user.email}`);
      if (user.must_change_password) {
        nav("/?forceChangePassword=1", { replace: true });
      } else {
        nav("/", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiError(err, "Sign-in failed"));
    } finally {
      setSubmitting(false);
    }
  };

  React.useEffect(() => {
    if (auth.isAuthed()) nav("/", { replace: true });
  }, [nav]);

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 bg-white" data-testid="login-page">
      {/* Left hero */}
      <div className="relative hidden md:block">
        <img src="/hospital-hero.webp" alt="Accredited healthcare provider" className="absolute inset-0 h-full w-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(10,72,49,0.86) 0%, rgba(14,92,61,0.78) 55%, rgba(5,38,24,0.72) 100%)" }}
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-md bg-white p-1.5">
              <img src="/nationwide-logo.png" alt="Nationwide Medical Insurance" className="h-full w-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-sm font-black uppercase tracking-wider">Nationwide Medical Insurance</div>
              <div className="text-[11px] font-medium uppercase tracking-[0.2em] text-white/70">Ghana · HSP Network Manager</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-4xl md:text-5xl font-black leading-none tracking-tight">
              Real-time control<br />over 1,500+ facilities.
            </h1>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/85">
              Suspend, reactivate, audit and alert — from the web or Telegram, in seconds.
              Every state change is broadcast to your team and permanently logged.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-4 text-[11px] font-bold uppercase tracking-wider text-white/70">
              <div>Auditable</div>
              <div>Broadcast</div>
              <div>Alternatives</div>
            </div>
          </div>
          <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50">© Nationwide Medical Insurance</div>
        </div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-8 md:p-16">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="mb-6 flex items-center gap-3 md:hidden">
            <img src="/nationwide-logo.png" alt="Nationwide" className="h-11 w-11 object-contain" />
            <div className="leading-tight">
              <div className="font-heading text-[12px] font-black uppercase tracking-wider text-nmi-700">Nationwide Medical Insurance</div>
              <div className="text-[10px] font-medium uppercase tracking-[0.2em] text-slate-500">HSP Network Manager</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-nmi-700">Restricted access</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
            <p className="mt-2 text-sm text-slate-600">
              Enter the email and password provided by your NMI administrator.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Email
              </label>
              <input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
                placeholder="you@nationwidemh.com"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-nmi-600 focus:ring-1 focus:ring-nmi-600"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="w-full rounded-md border border-slate-200 bg-white px-3 py-2.5 pr-10 text-sm outline-none focus:border-nmi-600 focus:ring-1 focus:ring-nmi-600"
                />
                <button
                  type="button"
                  aria-label={showPw ? "Hide password" : "Show password"}
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="login-password-toggle"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 hover:text-slate-700"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-nmi-700 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-nmi-800 disabled:opacity-60"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <LogIn size={16} />}
              Sign in
            </button>
          </div>

          <div className="mt-6 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] leading-relaxed text-slate-600">
            <b>Forgot your password?</b>{" "}
            <button
              type="button"
              data-testid="login-forgot-password"
              onClick={() => nav("/forgot-password")}
              className="font-bold text-nmi-700 underline hover:text-nmi-800"
            >
              Email me a reset link
            </button>
            .
          </div>

          <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-slate-500">
            <button
              type="button"
              data-testid="login-terms-link"
              onClick={() => nav("/terms")}
              className="hover:text-nmi-700 hover:underline"
            >
              Terms of Service
            </button>
            <span className="text-slate-300">·</span>
            <button
              type="button"
              data-testid="login-privacy-link"
              onClick={() => nav("/privacy")}
              className="hover:text-nmi-700 hover:underline"
            >
              Privacy Policy
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
